import { Fragment, type ReactNode } from "react";

import { outboundLinkProps, withMiraContext } from "./fiverr-link";

// A deliberately tiny inline **bold** parser, NOT a markdown library. MIRA and
// STERLING are prompted to use ONLY markdown bold, so this splits on paired **
// and wraps the inside in <strong>. Anything else (a lone *, an unmatched **,
// other marks) stays literal text. Segments are real React nodes, never
// dangerouslySetInnerHTML. Line breaks are left to the caller's CSS
// (white-space: pre-wrap).
//
// It also links a bare ABSOLUTE http(s) URL, so an address Mira needs a client
// to follow (e.g. Fiverr support in the blocked-account notice) is clickable.
// The scheme is required, so a domain she reads back ("is acme.com the right
// site?") stays plain text.

export type InlineSegment = { text: string; bold: boolean };

// One piece of a segment: plain text, or the same text as a link target.
export type InlinePiece = { text: string; href?: string };

// Non-greedy, requires at least one character between the markers, so `****`
// and a dangling `**` never match and pass through as literal text.
const BOLD_RE = /\*\*([\s\S]+?)\*\*/g;

// Pure + exported for unit testing: split text into bold / plain segments.
export function parseInlineBold(text: string): InlineSegment[] {
  const segments: InlineSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(BOLD_RE)) {
    const start = match.index ?? 0;
    if (start > last) segments.push({ text: text.slice(last, start), bold: false });
    segments.push({ text: match[1], bold: true });
    last = start + match[0].length;
  }
  if (last < text.length) segments.push({ text: text.slice(last), bold: false });
  return segments;
}

// An explicit scheme only, and the trailing sentence punctuation stays OUT of
// the href: "support can help: https://x.com/help." must link the URL and leave
// the period behind as text.
const URL_RE = /https?:\/\/\S+/g;
const TRAILING_PUNCT = /[.,;:!?)\]}'"]+$/;
const HAS_HOST = /^https?:\/\/\S/;

// Pure + exported for unit testing: split one segment's text into link / plain
// pieces, in order.
export function splitInlineLinks(text: string): InlinePiece[] {
  const pieces: InlinePiece[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    const start = match.index ?? 0;
    const url = match[0].replace(TRAILING_PUNCT, "");
    // A scheme with nothing but punctuation behind it is not a link; leave the
    // whole run to the plain-text slice below.
    if (!HAS_HOST.test(url)) continue;
    if (start > last) pieces.push({ text: text.slice(last, start) });
    pieces.push({ text: url, href: url });
    last = start + url.length;
  }
  if (last < text.length) pieces.push({ text: text.slice(last) });
  return pieces;
}

// Render text with **bold** spans as <strong> and absolute URLs as links.
// Fast-path the common case (nullish, or neither marker nor scheme) back to the
// raw input so an ordinary reply stays a plain text node. Accepts the same
// `string | undefined` a message's `text` field can be, so call sites drop in
// where a bare `{msg.text}` used to be.
export function renderInlineBold(text: string | null | undefined): ReactNode {
  if (text == null) return text;
  // Brief block payloads are untyped JSON on the server, so a non-string can
  // reach here despite the declared type. Stringify it: a throw in `.includes`
  // would blank the whole block.
  const value = typeof text === "string" ? text : String(text);
  if (!value || (!value.includes("**") && !value.includes("://"))) return value;
  return parseInlineBold(value).flatMap((seg, i) =>
    splitInlineLinks(seg.text).map((piece, j) => {
      const key = `${i}:${j}`;
      // The LINK TEXT stays exactly what Mira wrote; only the href carries the
      // attribution. A client who copies the visible address gets the clean one,
      // and the params never end up quoted back at her in the next turn.
      // A non-fiverr host falls through untouched. A new tab, except a
      // fiverr.com page inside the Fiverr app, which opens in place.
      const href = piece.href ? withMiraContext(piece.href, "agent_prose") : undefined;
      const body = href ? (
        <a href={href} {...outboundLinkProps(href)}>
          {piece.text}
        </a>
      ) : (
        piece.text
      );
      return seg.bold ? <strong key={key}>{body}</strong> : <Fragment key={key}>{body}</Fragment>;
    }),
  );
}

// Plain-text form for attribute strings (aria-label and friends, which take a
// string, not React nodes): the same paired-** spans with the markers REMOVED
// rather than wrapped — assistive tech should hear the question, not
// "star star". Unpaired/stray asterisks pass through untouched, exactly like
// renderInlineBold. Nullish in → undefined out, so `aria-label=` omits the
// attribute the same way the bare value would.
export function stripInlineBold(text: string | null | undefined): string | undefined {
  if (text == null) return undefined;
  const value = typeof text === "string" ? text : String(text);
  if (!value.includes("**")) return value;
  return parseInlineBold(value)
    .map((seg) => seg.text)
    .join("");
}
